<script setup>
/* Ein Schlagwort-Chip — dieselbe Sprache wie auf der Player Card (D7): getönte Fläche (Sky 18 %), Rand Sky 60 %,
   kleiner Sky-Punkt davor, halbfett. Im hellen Thema Sky Dark auf getönter heller Fläche.
   Drei Formen: Anzeige (Player Info), gewählt mit Entfernen-Knopf (removable, Auswahl), antippbar (selectable, die
   gruppierten Chips der Auswahl; 40 px hoch, aria-pressed, abgeblendet wenn disabled). */
import { mdiClose } from '@mdi/js';

defineProps({
  label: { type: String, required: true },
  removable: { type: Boolean, default: false },
  removeLabel: { type: String, default: '' },
  selectable: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
defineEmits(['remove', 'toggle']);
</script>

<template>
  <button
    v-if="selectable"
    type="button"
    class="kw-chip touch"
    :class="{ selected, off: disabled }"
    :aria-pressed="selected"
    :disabled="disabled"
    @click="$emit('toggle')"
  >
    <span class="kw-dot" aria-hidden="true"></span>
    <span class="kw-text">{{ label }}</span>
  </button>
  <span v-else class="kw-chip" :class="{ removable }">
    <span class="kw-dot" aria-hidden="true"></span>
    <span class="kw-text">{{ label }}</span>
    <button v-if="removable" type="button" class="kw-x" :aria-label="removeLabel" data-testid="keyword-remove" @click="$emit('remove')">
      <v-icon :icon="mdiClose" size="14" />
    </button>
  </span>
</template>

<style scoped>
.kw-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 12px 0 11px;
  border-radius: 15px;
  /* dunkel: Sky 18 % auf der Fläche, Rand Sky 60 % (wie auf der Karte: #27394B / #417F9B / #8CD5F0) */
  background: color-mix(in srgb, #5ac2e7 18%, var(--card, #1c1b29));
  border: 1px solid color-mix(in srgb, #5ac2e7 60%, var(--card, #1c1b29));
  color: #8cd5f0;
  font: inherit;
  font-size: var(--pv-meta, 13px);
  font-weight: 600;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.kw-chip.removable {
  padding-right: 4px;
}
/* antippbar: gut tippbar auf dem Handy (mindestens 40 px), ungewählt ruhig, gewählt kräftiger */
.kw-chip.touch {
  height: 40px;
  padding: 0 16px 0 14px;
  cursor: pointer;
  background: transparent;
  border-color: color-mix(in srgb, #5ac2e7 35%, var(--card, #1c1b29));
  color: color-mix(in srgb, #8cd5f0 85%, var(--card, #1c1b29));
}
.kw-chip.touch.selected {
  background: color-mix(in srgb, #5ac2e7 26%, var(--card, #1c1b29));
  border-color: #5ac2e7;
  color: #cfeefa;
}
.kw-chip.touch.off {
  opacity: 0.38;
  cursor: default;
}
.kw-chip.touch:focus-visible {
  outline: 2px solid #5ac2e7;
  outline-offset: 2px;
}
.kw-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #5ac2e7;
  flex: none;
}
.kw-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: 2px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.kw-x:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: -2px;
}
</style>

<!-- Ohne scoped, mit bewusst vollständigem Selektor. `:global(.v-theme--light) .kw-chip` im scoped-Block
     kompiliert zu einer nackten `.v-theme--light { … }`-Regel (der Nachfahre fällt weg) und färbt dann jedes
     Element mit Themenklasse ein — im hellen Thema alle Vuetify-Icons (D11). Eigene Klassen sind eindeutig. -->
<style>
/* hell: Sky Dark (#15739A) auf getönter heller Fläche */
.v-theme--light .kw-chip {
  background: color-mix(in srgb, #15739a 14%, #ffffff);
  border-color: color-mix(in srgb, #15739a 55%, #ffffff);
  color: #15739a;
}
.v-theme--light .kw-chip.touch {
  background: transparent;
  border-color: color-mix(in srgb, #15739a 35%, #ffffff);
  color: #15739a;
}
.v-theme--light .kw-chip.touch.selected {
  background: color-mix(in srgb, #15739a 18%, #ffffff);
  border-color: #15739a;
  color: #0e4f6b;
}
.v-theme--light .kw-dot {
  background: #15739a;
}
</style>
